/**
 * `@dxf-ai-editor/acadctb` — entry + lineweight-table builders.
 *
 * Consumes the {@link AsciiNode} tree produced by `./lexer.ts` and
 * projects the per-style fields onto the typed {@link PlotStyleEntry}
 * model.
 */

import {
  decodePlotStyleColor,
  parseBool,
  parseFloatField,
  parseIntField,
  parseStringField,
  parseUint32Field,
} from "./fields";
import { findLeaf } from "./lexer";
import { makeError } from "./errors";
import type { AsciiNode } from "./lexer";
import type { PlotStyleEntry, TableKind } from "./types";

/** AutoCAD ships exactly 256 ACI entries in every well-formed CTB. */
export const CTB_ENTRY_COUNT = 256;

/** Default lineweight (mm) for index 0 = "use object lineweight". */
export const DEFAULT_LINEWEIGHT_MM = 0.25;

export function buildPlotStyleEntry(
  children: AsciiNode[],
  fallbackName: string,
): PlotStyleEntry {
  const name = parseStringField(findLeaf(children, "name"), fallbackName);
  const description = parseStringField(findLeaf(children, "description"), "");
  // `color` is the packed override (see decodePlotStyleColor). The
  // separate `mode_color` field repeats the override for screen
  // preview — we honour `color` as the source of truth.
  const colorPacked = parseUint32Field(
    findLeaf(children, "color"),
    0xffffffff,
    "color",
  );
  const color = decodePlotStyleColor(colorPacked);
  const colorPolicyRaw = parseIntField(
    findLeaf(children, "color_policy"),
    color.useObject ? 0 : 1,
    "color_policy",
  );
  const colorPolicy: 0 | 1 = colorPolicyRaw === 1 ? 1 : 0;
  const physicalPenNumber = parseIntField(
    findLeaf(children, "physical_pen_number"),
    -1,
    "physical_pen_number",
  );
  const virtualPenNumber = parseIntField(
    findLeaf(children, "virtual_pen_number"),
    -1,
    "virtual_pen_number",
  );
  const screen = parseIntField(findLeaf(children, "screen"), 100, "screen");
  if (screen < 0 || screen > 100) {
    throw makeError(
      "field",
      `screen ${screen} out of range [0..100] for '${name}'`,
    );
  }
  const linepatternSize = parseFloatField(
    findLeaf(children, "linepattern_size"),
    0.5,
    "linepattern_size",
  );
  const linetypeIndex = parseUint32Field(
    findLeaf(children, "linetype"),
    31,
    "linetype",
  );
  const adaptiveLinetype = parseBool(
    findLeaf(children, "adaptive_linetype"),
    true,
  );
  const lineweightIndex = parseUint32Field(
    findLeaf(children, "lineweight"),
    0,
    "lineweight",
  );
  const fillStyle = parseUint32Field(
    findLeaf(children, "fill_style"),
    73,
    "fill_style",
  );
  const endStyle = parseUint32Field(
    findLeaf(children, "end_style"),
    4,
    "end_style",
  );
  const joinStyle = parseUint32Field(
    findLeaf(children, "join_style"),
    4,
    "join_style",
  );
  const plot = parseBool(findLeaf(children, "plot"), true);

  return {
    name,
    description,
    color,
    colorPolicy,
    physicalPenNumber,
    virtualPenNumber,
    screen,
    linepatternSize,
    linetypeIndex,
    adaptiveLinetype,
    lineweightIndex,
    fillStyle,
    endStyle,
    joinStyle,
    plot,
  };
}

export function buildLineweightTable(
  children: AsciiNode[] | undefined,
): number[] {
  // Index 0 is reserved for the "use object lineweight" sentinel; we
  // emit it even when the file does not include an entry for index 0.
  const result: number[] = [DEFAULT_LINEWEIGHT_MM];
  if (children === undefined) {
    return result;
  }
  // Lineweight blocks are typically keyed by their numeric index
  // (e.g. "0=0.25"); we sort by index to keep the array dense.
  const entries: { index: number; value: number }[] = [];
  for (const node of children) {
    if (node.kind !== "leaf") {
      continue;
    }
    const trimmed = node.key.trim();
    if (!/^\d+$/.test(trimmed)) {
      continue;
    }
    const index = Number.parseInt(trimmed, 10);
    const value = parseFloatField(
      node.value,
      0,
      `custom_lineweight_table.${trimmed}`,
    );
    entries.push({ index, value });
  }
  entries.sort((a, b) => a.index - b.index);
  for (const { index, value } of entries) {
    if (index === 0) {
      result[0] = value;
    } else {
      // Pad with 0 for any missing intermediate indices to keep the
      // array densely indexable by `lineweightIndex`.
      while (result.length < index) {
        result.push(0);
      }
      result[index] = value;
    }
  }
  return result;
}

export function buildEntries(
  block: AsciiNode[] | undefined,
  kind: TableKind,
): PlotStyleEntry[] {
  if (block === undefined) {
    if (kind === "ctb") {
      throw makeError("grammar", "CTB body missing 'plot_style' block");
    }
    throw makeError("grammar", "STB body missing 'plot_style' block");
  }
  // Each child should be a block whose key is the entry index
  // (numeric string for CTB; sequential numeric string for STB).
  const collected: { index: number; entry: PlotStyleEntry }[] = [];
  for (const node of block) {
    if (node.kind !== "block") {
      // Defensive: skip leaves the writer accidentally tucked inside.
      continue;
    }
    const indexTrim = node.key.trim();
    if (!/^\d+$/.test(indexTrim)) {
      throw makeError(
        "grammar",
        `plot_style child key '${node.key}' is not a numeric index`,
      );
    }
    const index = Number.parseInt(indexTrim, 10);
    const fallbackName =
      kind === "ctb" ? `Color_${index + 1}` : `Style_${index + 1}`;
    const entry = buildPlotStyleEntry(node.children, fallbackName);
    collected.push({ index, entry });
  }
  collected.sort((a, b) => a.index - b.index);
  const entries = collected.map((e) => e.entry);
  if (kind === "ctb") {
    if (entries.length !== CTB_ENTRY_COUNT) {
      throw makeError(
        "grammar",
        `CTB plot_style must contain exactly ${CTB_ENTRY_COUNT} entries; got ${entries.length}`,
      );
    }
  } else {
    if (entries.length === 0) {
      throw makeError("grammar", "STB plot_style is empty");
    }
    const hasNormal = entries.some((e) => e.name === "Normal");
    if (!hasNormal) {
      throw makeError(
        "grammar",
        "STB plot_style is missing the mandatory 'Normal' entry",
      );
    }
  }
  return entries;
}
